import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Bank account state for registration
 * Stores banking information for fund transfers
 */
export interface RegistrationBankState {
  bankName: string | null;
  accountNumber: string | null;
  accountBan: string | null;
  accountHolderName: string | null;
  swiftCode: string | null;
}

const initialState: RegistrationBankState = {
  bankName: null,
  accountNumber: null,
  accountBan: null,
  accountHolderName: null,
  swiftCode: null,
};

/**
 * Generic reducer factory for simple field updates
 * Eliminates duplication for setters that just assign a value
 */
const createFieldSetter = <K extends keyof RegistrationBankState>(field: K) => {
  return (state: RegistrationBankState, action: PayloadAction<RegistrationBankState[K]>) => {
    state[field] = action.payload;
  };
};

const bankSlice = createSlice({
  name: "bank",
  initialState,
  reducers: {
    // Bank account field setters - Unwrapped payload pattern
    setBankNameRedux: createFieldSetter("bankName"),
    setAccountNoRedux: createFieldSetter("accountNumber"),
    setAccountBanRedux: createFieldSetter("accountBan"),
    setAccountHolderNameRedux: createFieldSetter("accountHolderName"),
    setSwiftCodeReduct: createFieldSetter("swiftCode"),

    /**
     * Bulk update multiple fields at once
     * Useful for hydrating state from API response
     */
    hydrateRegistrationBankState: (
      state,
      action: PayloadAction<Partial<RegistrationBankState>>,
    ) => {
      return { ...state, ...action.payload };
    },

    /**
     * Reset to initial state
     */
    resetRegistrationBankState: () => initialState,
  },
});

export const {
  setBankNameRedux,
  setAccountNoRedux,
  setAccountBanRedux,
  setAccountHolderNameRedux,
  setSwiftCodeReduct,
  hydrateRegistrationBankState,
  resetRegistrationBankState,
} = bankSlice.actions;

export default bankSlice.reducer;
